import { useEffect, useRef, useState } from 'react'

/**
 * Tween a displayed number toward a target instead of snapping to it.
 *
 * Backend-resolved prices land asynchronously (debounced preview, recompute
 * broadcast), so a bare `{value}` makes figures flicker and jump — the operator
 * sees motion and can't tell whether the number changed or the page did. Easing
 * the transit reads as "this is updating" rather than "this blinked".
 *
 * This is DISPLAY SMOOTHING ONLY and never a source of truth: intermediate
 * frames are discarded, the tween always terminates exactly on the backend's
 * value, and callers still save `value`, never what is on screen. It does not
 * violate the frontend-does-no-rate-math rule for the same reason a fade doesn't
 * — no derived figure is ever read back out.
 *
 * Honours prefers-reduced-motion by assigning the target immediately.
 */
const DURATION_MS = 420

// Ease-out cubic: fast commit, gentle settle — the value feels "placed", not slid.
const easeOut = (t: number) => 1 - Math.pow(1 - t, 3)

export function useAnimatedNumber(value: number | null, durationMs = DURATION_MS): number | null {
  const [display, setDisplay] = useState<number | null>(value)
  const frameRef = useRef<number | null>(null)
  const fromRef = useRef<number | null>(value)

  useEffect(() => {
    if (value == null) {
      setDisplay(null)
      fromRef.current = null
      return
    }

    const reduced =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

    const from = fromRef.current
    // First paint, a reduced-motion user, or no actual change: assign directly.
    if (reduced || from == null || from === value) {
      fromRef.current = value
      setDisplay(value)
      return
    }

    const start = performance.now()
    const delta = value - from

    const step = (now: number) => {
      const t = Math.min(1, (now - start) / durationMs)
      if (t >= 1) {
        // Always terminate on the exact backend value — never an eased approximation.
        fromRef.current = value
        setDisplay(value)
        frameRef.current = null
        return
      }
      // The live position is parked in the ref every frame, so a change that
      // interrupts this tween starts from where the eye last saw the number
      // rather than teleporting back. (Reading `display` in the cleanup could
      // not do this: that closure only ever sees the value from the render the
      // effect ran in — the position at the START of the tween.)
      const current = from + delta * easeOut(t)
      fromRef.current = current
      setDisplay(current)
      frameRef.current = requestAnimationFrame(step)
    }

    frameRef.current = requestAnimationFrame(step)
    return () => {
      if (frameRef.current != null) cancelAnimationFrame(frameRef.current)
    }
  }, [value, durationMs])

  return display
}
